import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, UserCog } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CORPORATE_RE = /\b(ltd|limited|llc|inc|plc|corp|corporation|company|co\.|gmbh|sarl|bv|nv|pty|pvt|enterprises?|ventures?|group|holdings?|bank|nigeria|international|global|services|solutions|stores?|supermarket|hotel|pharmacy|clinic|hospital|school|university|church|foundation|trust|partners|associates|consulting|technologies|tech|industries|trading|logistics|energy|oil|gas|properties|realty|motors|airlines?|telecom|network|media|studio|agency|ministry|government|federal|state|council|authority|commission|board)\b/i;
/** Corporate vs individual from the counterparty name (same heuristic family as backend infer_entity_type_from_name). */
export const inferEntityTypeFromName = (name) => {
  const n = String(name || '').trim();
  if (!n) return 'corporate';
  if (CORPORATE_RE.test(n)) return 'corporate';
  const words = n.split(/\s+/);
  return words.length <= 3 && words.every((w) => /^[A-Za-z'.-]+$/.test(w)) ? 'individual' : 'corporate';
};

/** Inline tax-profile prompt on the bill / invoice form.
 *  • existing contact missing the facts the engine needs → amber panel, save inline (PUT /contacts/{id})
 *  • new counterparty (name typed, no contact yet) → create the contact WITH the facts (POST /contacts) so the
 *    tax treatment is right from the first document. "Ignore" is allowed but the implications are spelled out. */
export const ContactTaxProfilePrompt = ({ contactId, contactName, contactType = 'vendor', entityType, onContactSaved, className = '' }) => {
  const [schema, setSchema] = useState(null);
  const [contact, setContact] = useState(null);
  const [form, setForm] = useState({ vat_registered: null, non_resident: false, export_customer: false, wht_deducting: false, tin: '', entity_type: entityType || 'corporate' });
  const [ignored, setIgnored] = useState(false);
  const [busy, setBusy] = useState(false);
  const isVendor = contactType === 'vendor';

  useEffect(() => { axios.get(`${API}/contacts/tax-attributes-schema`).then((r) => setSchema(r.data)).catch(() => setSchema({ fields: [] })); }, []);
  useEffect(() => {
    setIgnored(false);
    if (!contactId) { setContact(null); setForm((f) => ({ ...f, entity_type: entityType || inferEntityTypeFromName(contactName) })); return; }
    axios.get(`${API}/contacts/${contactId}`).then((r) => {
      const c = r.data?.contact || r.data || {};
      setContact(c);
      const ta = c.tax_attributes || {};
      if (ta.profile_prompt_dismissed) setIgnored(true);
      setForm({ vat_registered: ta.vat_registered ?? c.vat_registered ?? null, non_resident: !!ta.non_resident, export_customer: !!ta.export_customer,
                wht_deducting: !!ta.wht_deducting, tin: ta.tin || c.tax_id || '', entity_type: ta.entity_type || c.entity_type || entityType || inferEntityTypeFromName(c.name || contactName) });
    }).catch(() => setContact(null));
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!schema || ignored) return null;
  const name = (contactName || '').trim();
  if (!contactId && !name) return null;
  const ta = contact?.tax_attributes || {};
  const hasWht = !!schema.has_wht;
  const missing = [];
  if (isVendor) {
    if (contactId ? (ta.vat_registered === undefined || ta.vat_registered === null) && contact?.vat_registered == null : true) missing.push(`${schema.tax_label || 'VAT'} registration`);
    if (contactId ? ta.non_resident === undefined : true) missing.push('residency');
    if (hasWht && !(ta.tin || contact?.tax_id)) missing.push('TIN');
  } else {
    if (contactId ? ta.export_customer === undefined && ta.non_resident === undefined : true) missing.push('export / domestic');
    if (hasWht && (contactId ? ta.wht_deducting === undefined : true)) missing.push('deducts WHT?');
  }
  if (contactId && !contact) return null;
  if (!missing.length) return null;

  const save = async () => {
    setBusy(true);
    try {
      const tax_attributes = { vat_registered: form.vat_registered, non_resident: !!form.non_resident, entity_type: form.entity_type, tin: form.tin || null,
                               ...(isVendor ? {} : { export_customer: !!form.export_customer, wht_deducting: !!form.wht_deducting }) };
      Object.keys(tax_attributes).forEach((k) => tax_attributes[k] == null && delete tax_attributes[k]);
      let saved;
      if (contactId) {
        const r = await axios.put(`${API}/contacts/${contactId}`, { tax_attributes, entity_type: form.entity_type, ...(form.tin ? { tax_id: form.tin } : {}) });
        saved = r.data?.contact || r.data || { id: contactId, name };
      } else {
        const r = await axios.post(`${API}/contacts`, { name, contact_type: contactType, entity_type: form.entity_type, tax_id: form.tin || null, tax_attributes });
        saved = r.data?.contact || r.data;
      }
      toast.success(`${isVendor ? 'Supplier' : 'Customer'} tax profile saved — used on this and every future document`);
      setContact({ ...(saved || {}), tax_attributes });
      onContactSaved && onContactSaved({ ...(saved || {}), id: saved?.id || contactId, name: saved?.name || name, entity_type: form.entity_type, tax_attributes });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the tax profile');
    } finally { setBusy(false); }
  };

  const implications = isVendor
    ? `Without these, no input ${schema.tax_label || 'VAT'} is claimed on this ${isVendor ? 'bill' : 'invoice'}${hasWht ? ', WHT may be deducted at the no-TIN penalty rate,' : ''} and reverse-charge rules cannot be applied.`
    : `Without these, the sale is treated as domestic at the standard rate${hasWht ? ' and no WHT receivable is expected' : ''}.`;

  return (
    <div className={`rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs space-y-2 ${className}`} data-testid="contact-tax-profile-prompt">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-900">
            {contactId ? `Tax profile incomplete for ${contact?.name || name}` : `New ${isVendor ? 'supplier' : 'customer'} "${name}" — set the tax facts once`} · missing: {missing.join(', ')}
          </p>
          <p className="text-amber-800 mt-0.5">{implications}</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="flex items-center justify-between gap-2 bg-white/70 rounded px-2 py-1">
          <span>{schema.tax_label || 'VAT'} registered</span>
          <Switch checked={form.vat_registered === true} onCheckedChange={(v) => setForm({ ...form, vat_registered: v })} data-testid="ctp-vat-registered" />
        </label>
        <label className="flex items-center justify-between gap-2 bg-white/70 rounded px-2 py-1">
          <span>Based abroad</span>
          <Switch checked={!!form.non_resident} onCheckedChange={(v) => setForm({ ...form, non_resident: v })} data-testid="ctp-non-resident" />
        </label>
        {!isVendor && (
          <label className="flex items-center justify-between gap-2 bg-white/70 rounded px-2 py-1">
            <span>Export customer</span>
            <Switch checked={!!form.export_customer} onCheckedChange={(v) => setForm({ ...form, export_customer: v })} data-testid="ctp-export" />
          </label>
        )}
        {!isVendor && hasWht && (
          <label className="flex items-center justify-between gap-2 bg-white/70 rounded px-2 py-1">
            <span>Deducts WHT from our invoices</span>
            <Switch checked={!!form.wht_deducting} onCheckedChange={(v) => setForm({ ...form, wht_deducting: v })} data-testid="ctp-wht-deducting" />
          </label>
        )}
        <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
          <SelectTrigger className="h-8 bg-white" data-testid="ctp-entity-type"><SelectValue placeholder="Entity type" /></SelectTrigger>
          <SelectContent>{['corporate', 'individual', 'government', 'nonprofit'].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} placeholder={hasWht ? 'TIN (avoids no-TIN penalty WHT rate)' : 'Tax ID (optional)'} className="h-8 bg-white" data-testid="ctp-tin" />
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" disabled={busy} onClick={save} className="h-7 bg-[#108981] hover:bg-[#0d7a73]" data-testid="ctp-save">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCog className="w-3 h-3 mr-1" />}{contactId ? 'Save tax profile' : `Create ${isVendor ? 'supplier' : 'customer'} with these facts`}
        </Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 text-amber-800" data-testid="ctp-ignore"
                onClick={() => { setIgnored(true); toast.warning(implications, { duration: 6000 });
                                 if (contactId) axios.put(`${API}/contacts/${contactId}`, { tax_attributes: { profile_prompt_dismissed: true } }).catch(() => {}); }}>
          Ignore for now
        </Button>
      </div>
    </div>
  );
};

export default ContactTaxProfilePrompt;
